/**
 * Validate cart items against live menu data.
 *
 * Returns which items no longer exist (Case 2: auto-remove) and which
 * items exist but have stale modifier IDs (Case 1: re-select modal).
 *
 * Safe to call with empty inputs — returns valid=true when menuCategories
 * is empty because we cannot validate without menu data.
 *
 * @param {Array} cartItems - Items currently in the cart
 * @param {Array} menuCategories - Live categories from CloverContext menuData.categories
 * @returns {{ valid: boolean, invalidItems: Array, invalidModifiers: Array }}
 */
export function validateCart(cartItems, menuCategories) {
  const result = {
    valid: true,
    invalidItems: [],
    invalidModifiers: [],
  };

  if (!cartItems.length || !menuCategories.length) return result;

  // Flatten all menu items for O(1) lookup
  const menuItemsById = new Map();
  for (const category of menuCategories) {
    for (const item of category.items || []) {
      menuItemsById.set(item.id, item);
    }
  }

  // Build a set of all valid modifier IDs per menu item
  const validModifierIdsByItemId = new Map();
  for (const [itemId, item] of menuItemsById) {
    const modifierIds = new Set();
    for (const group of item.modifierGroups || []) {
      for (const mod of group.modifiers || []) {
        modifierIds.add(mod.id);
      }
    }
    validModifierIdsByItemId.set(itemId, modifierIds);
  }

  for (const cartItem of cartItems) {
    const menuItem = menuItemsById.get(cartItem.baseItem?.id);

    if (!menuItem) {
      result.valid = false;
      result.invalidItems.push(cartItem);
      continue;
    }

    const validModifierIds =
      validModifierIdsByItemId.get(menuItem.id) || new Set();
    const cartModifiers = cartItem.modifiers || [];

    // Only validate modifier IDs when the item actually has modifier groups in the menu
    const menuHasModifiers = (menuItem.modifierGroups || []).some(
      (g) => (g.modifiers || []).length > 0,
    );

    if (menuHasModifiers && cartModifiers.length > 0) {
      const staleModifierIds = cartModifiers
        .map((m) => m.id)
        .filter((id) => !validModifierIds.has(id));

      if (staleModifierIds.length > 0) {
        result.valid = false;
        result.invalidModifiers.push({
          cartItem,
          staleModifierIds,
          freshItem: menuItem,
        });
      }
    }
  }

  return result;
}
